import React, { useState, useEffect } from "react";
import { Box, Button, Flex, FormControl, FormErrorMessage, Heading, Select, Text, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { mapSelectedRoundToFormDefaults, validateReportingDates } from "../ISSB-helpers";
import { useGetOrganizationEntities } from "../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { showErrorToast, showSuccessToast } from "../../../utils";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRefreshRoundList, useSelectRound } from "./RoundService";
import { routes } from "../../../config";
import A from "components/A";
import { FormInput, FormSelect } from "components/Forms";
import FormMultiSelect from "components/Forms/FormMultiSelect";
import questionnaireService from "api/issb/questionnaire.service";
import templatePackageService from "api/issb/templatepackage.service";

import { useAuth } from "context";
import EsgFormLabel from "components/Forms/EsgFormLabel";
import useAirUserRoles from "utils/AuthUtils";
import FormSelectUser from "../FormSelectUser";
import IssbContainer from "../IssbContainer";

import SECTORS_DATA from "data/sectors.json";
import MultiSelect from "components/MultiSelect";
import { prettyPrintCodeName, toArray } from "utils/text-utils";

const IssbQuestionnaireRoundEditorPage = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "issb.questionnaire.setup",
  });

  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get("edit") === "true";

  const toast = useToast();
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth();

  const { refreshRoundList } = useRefreshRoundList();
  const selectRound = useSelectRound();
  const [entityOptions, setEntityOptions] = useState([]);

  const { isOrgAdmin, isDeveloper, isESGAdmin } = useAirUserRoles();
  const { user } = useAuth();

  const defaultValues = {
	_owners: user?.email ? [{ value: user.email, label: user.email }] : [],
  };
//   console.log("defaultValues!", defaultValues);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    control,
    reset,
  } = useForm({
	defaultValues
  });

  const startYear = watch("startYear");
  const startMonth = watch("startMonth");
  const endYear = watch("endYear");
  const endMonth = watch("endMonth");

  const _packageId = watch("_package");

  const months = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(0, i);
    const monthName = date.toLocaleString("en", { month: "long" });
    return { value: i, label: monthName };
  });

  const roundSelector = useSelector((state) => state.issbRound);

  useEffect(() => {
    if (isEditMode) {
      const selectedRound = roundSelector.selectedRound;
      // TODO fix bugs when editing an existing round - not all properties are handled.
      // refactor to an edit-the-object approach is more flexible, and less code.
      const defaultValues = mapSelectedRoundToFormDefaults(selectedRound, [], []);
      reset(defaultValues);
    } else {
      console.log("not edit mode");
      reset({});
    }
  }, [entityOptions, isEditMode, reset, roundSelector.selectedRound]);

  const [packages, setPackages] = useState([]);
  useEffect(() => {
    getAccessTokenSilently().then((token) => {
      templatePackageService.list().then((packagesPage) => {
        console.log("packages!", packagesPage);
        setPackages(packagesPage.content);
      });
    });
  }, [getAccessTokenSilently]);

  const _package = _packageId && packages?.find((p) => p.id === _packageId);

  useGetOrganizationEntities({
    onSuccess: (data) => {
      const entities = data.map((entity) => {
        return {
          value: entity?.id,
          label: entity?.name,
        };
      });
      setEntityOptions(entities);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
    enabled: true,
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const createQuestionnaire = (data) => {
    console.log("createQuestionnaire! data:", data);
    const qround = { ...data };
    // undo select value={value,label}
    qround.entities = data._entities.map((entity) => entity.value);
    delete qround._entities;
    qround.ownerEmails = data._owners.map((owner) => owner.value);
    delete qround._owners;
    qround.packageId = data._package;
    if (!data._package && packages.length === 1) {
      console.info("No package selected, using only package TODO if one then auto-set the select");
      qround.packageId = packages[0].id;
    }
    delete qround._package;
    qround.sectors = data.sectors?.map((sector) => sector.value);
    qround.tags = data.tags?.map((tag) => tag.value);
    // console.log("createQuestionnaire!", qround);
    dispatch(showAppSpinner());
    questionnaireService
      .create(qround)
      .then((qround2) => {
        refreshRoundList().then(() => {
          selectRound(qround2);
          showSuccessToast(toast, t("message.success"));
          setTimeout(() => {
            navigation(routes.issbRequirements);
          }, 500);
        });
      })
      .catch((err) => {
        console.error(err);
        showErrorToast(toast, t("message.error"));
      })
      .finally(() => {
        dispatch(hideAppSpinner());
      });
  };

  // if not org admin, redirect to requirements page
  useEffect(() => {
    if (!isOrgAdmin) {
      showErrorToast(toast, "Sorry: Only Organisation Admins can create new reporting rounds");
      navigation(routes.issbRequirements);
    }
  }, [isOrgAdmin, navigation, toast]);

  // Lock most settings after creation, but allow super-users to edit rounds later
  const isSetupDisabled = isEditMode && !(isDeveloper() || isESGAdmin);

  return (
    <IssbContainer margin="0">
      <Box
        as="form"
        onSubmit={handleSubmit(createQuestionnaire)}
        onReset={() => navigation(routes.issbRequirements)}
        display="flex"
        flexDir="column"
        gap={6}
        maxW="600px"
        mx="auto"
        p="6"
      >
        <Heading as="h2">
          <Text size="body.m" color="limeShade.700">
            {isEditMode ? "Edit" : "Set up"}
          </Text>
          <Text variant="heading">{isEditMode ? "Edit reporting round" : "Start a new reporting round"}</Text>
        </Heading>
        <Box>
          <FormInput
            size="lg"
            maxInlineSize="full"
            label="Name of the reporting round"
            placeholder={t("inputs.namePlaceholder")}
            {...register("name", { required: "Name is required" })}
            errors={errors}
          />
        </Box>

        {/* Package */}
        <Box>
          <FormSelect
            disabled={isEditMode}
            maxInlineSize="full"
            label="Framework"
            {...register("_package", { required: "A framework is required" })}
            size="lg"
            errors={errors}
          >
            <option value="" disabled selected>
              Select a framework
            </option>
            {packages?.map((pkg) => (
              <option key={pkg.id} value={pkg.id}>
                {pkg.name}
              </option>
            ))}
          </FormSelect>
        </Box>
        {_package?.settings
          ?.isSetSectorsExplicitly /* HACK for CDP allow sectors to be set on round start (not sure why we cant reuse the entity approach) */ && (
          <Box>
            <FormMultiSelect
              label="Sectors"
              name="sectors"
              control={control}
              options={SECTORS_DATA.map((sector) => ({
                label: prettyPrintCodeName(sector.name),
                value: sector.name,
              }))}
              size="lg"
              errors={errors}
              isDisabled={isSetupDisabled}
            />
          </Box>
        )}
        {_package?.settings?.optionalRoundTags?.length > 0 /* HACK for CDP allow picking the theme */ && (
          <Box>
            <FormMultiSelect
              label="Tags"
              name="tags"
              control={control}
              options={toArray(_package.settings.optionalRoundTags).map((tag) => ({
                label: tag,
                value: tag,
              }))}
              size="lg"
              errors={errors}
              isDisabled={isSetupDisabled}
            />
          </Box>
        )}

        {/* Owner / Controller */}
        <Box>
          <FormSelectUser
            label="Controllers"
            name="_owners"
            control={control}
            rules={{ required: "A Controller is required" }}
            size="lg"
            errors={errors}
          />
        </Box>

        {/* Entity(s) */}
        <Box>
          <FormMultiSelect
            label="Entities"
            name="_entities"
			id="select_entities"
            control={control}
            rules={{ required: "Entity is required" }}
            options={entityOptions}
            size="lg"
            errors={errors}
            isDisabled={isSetupDisabled}
          />
          {!entityOptions.length && (
            <Text>
              No entities found. You can add them in{" "}
              <A href="/app/settings/entities">Settings &gt; Entity Management</A>
            </Text>
          )}
        </Box>

        {/* Start Date */}
        <Flex gap="4" justifyContent="space-between">
          <Box>
            <FormControl isInvalid={errors.dateRange}>
              <EsgFormLabel>{t("inputs.start")}</EsgFormLabel>
              <Flex gap="4">
                <Select
                  size="lg"
                  placeholder={t("inputs.startMonthPlaceholder")}
                  // value={startMonth}
                  // onChange={(e) => setStartMonth(e.target.value)}
                  {...register("startMonth", {
                    required: "Start month is required",
                  })}
                  maxW="120px"
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </Select>
                <Select
                  size="lg"
                  placeholder={t("inputs.startYearPlaceholder")}
                  {...register("startYear", {
                    required: "Start year is required",
                  })}
                  maxW="120px"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
              </Flex>
              <FormErrorMessage>
                {errors.startMonth?.message} <br />
                {errors.startYear?.message}
              </FormErrorMessage>
            </FormControl>
          </Box>
          {/* End Date */}
          <Box>
            <FormControl isInvalid={errors.dateRange}>
              <EsgFormLabel>{t("inputs.end")}</EsgFormLabel>
              <Flex gap="4">
                <Select
                  size="lg"
                  placeholder={t("inputs.endMonthPlaceholder")}
                  {...register("endMonth", {
                    required: "End month is required",
                  })}
                  maxW="120px"
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </Select>
                <Select
                  size="lg"
                  placeholder={t("inputs.endYearPlaceholder")}
                  {...register("endYear", {
                    required: "End year is required",
                  })}
                  maxW="120px"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
              </Flex>
              <FormErrorMessage>
                {errors.endMonth?.message} <br />
                {errors.endYear?.message}
              </FormErrorMessage>
            </FormControl>
          </Box>
        </Flex>
        <FormControl isInvalid={errors.dateRange}>
          {/*Hidden field for validation*/}
          <FormErrorMessage>{errors?.dateRange?.message}</FormErrorMessage>
          <input
            type="hidden"
            {...register("dateRange", {
              validate: () => validateReportingDates(startYear, startMonth, endYear, endMonth),
            })}
          />
        </FormControl>

        {/* Buttons */}
        <Flex gap={4}>
          <Button type="reset" size="lg" variant="secondary" width="100%">
            {t("buttons.skip")}
          </Button>
          <Button type="submit" size="lg" variant="primary" width="100%">
            {t("buttons.save")}
          </Button>
        </Flex>
      </Box>
    </IssbContainer>
  );
};

export default IssbQuestionnaireRoundEditorPage;
